import type {ReactElement} from "react";
import type React from "react";
import {Skeleton, Stack, Typography, Box} from "@mui/material";
import {isAxiosError} from "axios";
import {PageHeader} from "~/components/main/header/PageHeader.tsx";
import {generatePath, Outlet, useNavigate, useParams} from "react-router";
import {BtnGroupTabs, type BtnGroupTabItem} from "~/components/BtnGroupTabs/BtnGroupTabs";
import {StatChip, type StatChipProps} from "~/components/StatChip/StatChip";
import {COLORS} from "~/style/colors-ts";
import {routes} from "../../routing/routes";
import type {Supplier360RouteMatch} from "../../routing/catRoutes";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent";
import {useSupplierEntityQuery} from "./hooks/useSupplierEntityQuery";
import {useSupplierKpiSummaryQuery} from "./hooks/useSupplierKpiSummaryQuery";
import type {SupplierKpiSummary} from "./types/Supplier360Types";
import {SUPPLIER_DETAIL_SCROLL_ID} from "./constants/supplierDetailScroll";
import {RevenueShareChip} from "./components/pdl/RevenueShareChip";
import DbIcon from "~/icons/svg/DbIcon.svg?react";
import DollarCoinIcon from "~/icons/svg/DollarCoinIcon.svg?react";
import ShieldIcon from "~/icons/svg/ShieldIcon.svg?react";
import ChartIcon from "~/icons/svg/ChartIcon.svg?react";
import DangerIcon from "~/icons/svg/DangerIcon.svg?react";
import NoteIcon from "~/icons/svg/NoteIcon.svg?react";
import InfoIcon from "~/icons/svg/InfoIcon.svg?react";
import TransactionIcon from "~/icons/svg/TransactionIcon.svg?react";
import ValueChainIcon from "~/icons/svg/ValueChainIcon.svg?react";
import {DRAWER_HEADER_HEIGHT_LG} from "~/components/main/drawer/DrawerConstants.ts";

const TABS: BtnGroupTabItem[] = [
    {title: 'Overview', Icon: InfoIcon, route: 'overview'},
    {title: 'Transactions', Icon: TransactionIcon, route: 'transactions'},
    {title: 'Contracts', Icon: NoteIcon, route: 'contracts'},
    {title: 'Opportunities', Icon: ChartIcon, route: 'opportunities'},
    {title: 'Compliance', Icon: ShieldIcon, route: 'compliance'},
    {title: 'Insights', Icon: DangerIcon, route: 'insights'},
    {title: 'Value chain', Icon: ValueChainIcon, route: 'value-chain'},
];


/**
 * `kpi_summary` (§2.1) backs 4 of these 7. The other 3 have no model behind them at all —
 * the `contracts` app is a schema-less stub, the scored-KPI panel "On-time" comes from was
 * never built, and no `Opportunity` model exists — so they stay placeholders rather than
 * showing a number the API would have had to invent (§3.3, §3.5, §3.6).
 */
const KPIS: {label: string, icon: ReactElement, color: string}[] = [
    {label: 'Spend', icon: <DollarCoinIcon/>, color: COLORS.success.main},
    {label: 'Transactions', icon: <DbIcon/>, color: COLORS.secondary.main},
    // {label: 'Active contracts', icon: <NoteIcon/>, color: COLORS.info.main},
    // {label: 'On-time', icon: <VerifiedIcon/>, color: COLORS.success.main},
    {label: 'Risk', icon: <DangerIcon/>, color: COLORS.warning.main},
    // {label: 'Open opportunities', icon: <ChartIcon/>, color: COLORS.info.main},
    {label: 'Compliant BUs', icon: <ShieldIcon/>, color: COLORS.success.main},
];

const NO_VALUE = '—';

/**
 * A pill's value, or `NO_VALUE` while the aggregate is still loading.
 *
 * `risk_score: 0` and `total_bus: 0` both mean "nothing computed yet" rather than a real zero
 * (§2.1), so they render as placeholders — a "Risk: 0" pill would read as *no risk at all*.
 */
function kpiValue(label: string, kpi: SupplierKpiSummary | undefined): React.ReactNode {
    if (!kpi) return NO_VALUE;
    switch (label) {
        case 'Spend':
            return <CurrencyComponent v={kpi.spend}/>;
        case 'Transactions':
            return kpi.transactions.toLocaleString();
        case 'Risk':
            return kpi.risk_score > 0 ? kpi.risk_score : NO_VALUE;
        case 'Compliant BUs':
            return kpi.total_bus > 0 ? `${kpi.compliant_bus}/${kpi.total_bus}` : NO_VALUE;
        default:
            return NO_VALUE;
    }
}

function buildKpiChips(kpi: SupplierKpiSummary | undefined): StatChipProps[] {
    return KPIS.map(({label, icon, color}) => {
        return {
            icon: {element: icon, color: color},
            title: label,
            value: kpiValue(label, kpi),
        };
    });
}

const ContainerSx = {height: `calc(100vh - ${DRAWER_HEADER_HEIGHT_LG}px)`, overflow: 'auto', position: 'relative'};

/** Shared layout for a supplier's detail page: resolves :supplierId, renders the stat-chip row + URL-synced tabs, and an <Outlet/> for the active tab. */
export const SupplierDetailLayout: React.FC = () => {
    const {id: databagId, supplierId} = useParams<Supplier360RouteMatch>();
    const navigate = useNavigate();
    const {data: entity, isLoading, isError, error} = useSupplierEntityQuery(supplierId);
    // Detail is `GET supplier_360/entity/{id}/` with no databag scope. A supplier from another bag
    // still loads, so it only counts as this route's supplier when `databag_id` matches `:id`.
    const routeDatabagId = Number(databagId);
    const matchedEntity = entity && Number.isFinite(routeDatabagId) && entity.databag_id === routeDatabagId
        ? entity
        : undefined;
    const {data: kpiSummary, isLoading: isKpiSummaryLoading} = useSupplierKpiSummaryQuery(matchedEntity?.id);

    const registryPath = generatePath(routes.supplier_360, {id: databagId ?? ''});
    // Back link and supplier name live in the app's top bar (Mithra 3.1 header)
    const backButton = <PageHeader
        back={{label: 'Supplier 360', onClick: () => navigate(registryPath)}}
        title={matchedEntity?.canonical_name}
    />;

    if (isLoading) {
        return  <Box sx={ContainerSx}>
            {backButton}
            <Stack gap={2} sx={{minHeight: `calc(100vh - ${DRAWER_HEADER_HEIGHT_LG}px)`, p: 2, position: 'relative'}}>
                <Stack direction="row" flexWrap="wrap" useFlexGap gap="10px">
                    {Array.from({length: 4}).map((_, i) => <Skeleton key={i} variant="rectangular" sx={{flex:1, borderRadius: 1}} height={34} />)}
                </Stack>
                <Stack direction="row" flexWrap="wrap" useFlexGap gap="10px">
                    {Array.from({length: 7}).map((_, i) => <Skeleton key={i} variant="rectangular" width={100} sx={{borderRadius: 1}} height={34} />)}
                </Stack>
                <Skeleton variant="rectangular" sx={{width: '100%', height: `calc(100vh - ${DRAWER_HEADER_HEIGHT_LG}px)`, borderRadius: 1}} />
            </Stack>
        </Box>;
    }

    if (isError || !matchedEntity) {
        const isNotFound = !isError || (isAxiosError(error) && error.response?.status === 404);
        return <Box sx={ContainerSx}>
            {backButton}
            <Stack gap={2}  alignItems="center" sx={{minHeight: `calc(100vh - ${DRAWER_HEADER_HEIGHT_LG}px)`, p: 2, mt:3, position: 'relative'}}>
                <Typography component="h6" variant="h6" fontWeight={400}>
                    {isNotFound ? 'Supplier not found' : 'Could not load supplier'}
                </Typography>
                <Typography color="text.secondary">
                    {isNotFound
                        ? ''
                        : 'An error occurred while fetching the supplier data. Please try again later.'}
                </Typography>
            </Stack></Box>
    }

    return (
        <Box id={SUPPLIER_DETAIL_SCROLL_ID} sx={ContainerSx}>
            {backButton}
            <Stack gap={2} sx={{p: 2, position: 'relative'}}>
                {/* The name is in the top bar; its logo and editor head the Overview tab's identity card. */}
                {isKpiSummaryLoading && <Stack direction="row" flexWrap="wrap" useFlexGap gap="10px">
                    <Skeleton variant="rounded" width={150} height={36} />
                    <Skeleton variant="rounded" width={150} height={36} />
                    <Skeleton variant="rounded" width={150} height={36} />
                    <Skeleton variant="rounded" width={150} height={36} />
                </Stack>}
                {kpiSummary && <Stack direction="row" flexWrap="wrap" useFlexGap gap="10px">
                    {buildKpiChips(kpiSummary).map((props, i) => <StatChip key={i} {...props}/>)}
                    <RevenueShareChip entityId={matchedEntity.id}/>
                </Stack>}
                {/* Sticky tabs + outlet share a wrapper so Stack `gap` isn't between them.
                    The tabs' own `pb` is then the only spacer, and it stays put while content scrolls under. */}
                <div>
                    <BtnGroupTabs items={TABS} sx={{position: 'sticky', top: 0, backgroundColor: COLORS.white[900], zIndex: 1000, pb:2}}/>
                    <Outlet context={{entity: matchedEntity}}/>
                </div>
            </Stack>
        </Box>

    );
};
